// Fluent System Icons (Microsoft, MIT licence; see ../icons.LICENSE), vendored as
// 20px regular + filled SVG paths from @fluentui/svg-icons 1.1.343. One module per
// icon, all grouped into one `design-icons` chunk (vite.config.ts). Only icons
// the navigation shows selected carry a filled variant; for the rest `filled`
// falls back to the regular outline, to keep the chunk small.
//
// Unlike ecm-frontend's plain-<svg> version, these render through MUI's SvgIcon,
// so every prop the replaced @mui/icons-material icons took (fontSize="small",
// color="primary", sx, htmlColor, titleAccess) keeps working unchanged.
//
// Usage: <FolderIcon />, <FolderIcon filled />, <FolderIcon size={20} />.
import * as React from "react";
import SvgIcon, { type SvgIconProps } from "@mui/material/SvgIcon";

export type FluentIconProps = SvgIconProps & {
  /** Width/height; a number is px. Overrides `fontSize`. */
  size?: number | string;
  /** Use the filled variant (the selected state). */
  filled?: boolean;
  /** Accessible name. Without it the icon is decorative (aria-hidden). */
  title?: string;
};

export type FluentIcon = React.ForwardRefExoticComponent<FluentIconProps & React.RefAttributes<SVGSVGElement>>;

export const createIcon = (name: string, regular: string, filled: string = regular): FluentIcon => {
  const Icon = React.forwardRef<SVGSVGElement, FluentIconProps>(
    ({ size, filled: isFilled, title, titleAccess, sx, ...rest }, ref) => (
      <SvgIcon
        ref={ref}
        viewBox="0 0 20 20"
        titleAccess={title ?? titleAccess}
        sx={size === undefined ? sx : [{ fontSize: size }, ...(Array.isArray(sx) ? sx : [sx])]}
        {...rest}
      >
        <path d={isFilled ? filled : regular} />
      </SvgIcon>
    ),
  );
  Icon.displayName = `${name}Icon`;
  // Lets MUI treat it like a createSvgIcon() icon (e.g. muiName checks).
  (Icon as unknown as { muiName: string }).muiName = "SvgIcon";
  return Icon;
};
